import { useState, useEffect } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { newItemDerivedFrom } from "@shared-core/domain/catalogue-merchandising";
import type {
  Catalogue,
  CatalogueItem,
  Narrowing,
} from "@shared-core/data/catalogue-types";
import { NarrowingEditor } from "./NarrowingEditor";
import { Card } from "../../primitives/card";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { currentTime } from "@shared-core/domain/current-time";

// The compose-a-new-catalogue-item form: pick an existing catalogue item to
// derive from, name it, narrow its spec, plus a create/cancel rail. Builds a
// CatalogueItem and hands it to the host via onCreate; the host decides where
// it goes (the catalogue editor's working copy).
//
// Derivation may only narrow the parent's resolved space, never add to it,
// so this offers a NarrowingEditor over the parent's real fields/breaks
// (loaded via the API). A brand-new manufacturable product is a dictionary
// item, and dictionary items are code (spec 03 §3.1) — there is no
// from-scratch path here.

export function CatalogueItemCreator({
  scope,
  submitLabel = "Create item",
  onCreate,
  onCancel,
}: {
  /** Catalogue the new item belongs to. */
  scope: string;
  submitLabel?: string;
  onCreate: (item: CatalogueItem) => void;
  onCancel: () => void;
}) {
  const adapter = useDataAdapter();
  const [catalogues, setCatalogues] = useState<Catalogue[]>([]);
  const [name, setName] = useState("");
  const [sourceScope, setSourceScope] = useState("");
  const [sourceItems, setSourceItems] = useState<CatalogueItem[]>([]);
  const [baseItemId, setBaseItemId] = useState("");
  const [baseItem, setBaseItem] = useState<CatalogueItem | null>(null);
  const [loadingCatalogues, setLoadingCatalogues] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingBase, setLoadingBase] = useState(false);
  // The narrowing built against baseItem's resolved fields/breaks — the only
  // spec-shaping affordance (derivation may only narrow, never add).
  const [narrowing, setNarrowing] = useState<Narrowing>({});

  // Load catalogue list once on mount
  useEffect(() => {
    adapter
      .listCataloguesForManagement()
      .then((cats) => {
        setCatalogues(cats);
        if (cats.length > 0) setSourceScope(cats[0].id);
      })
      .finally(() => setLoadingCatalogues(false));
  }, [adapter]);

  // Load items when the source catalogue changes
  useEffect(() => {
    if (!sourceScope) return;
    setLoadingItems(true);
    setSourceItems([]);
    setBaseItemId("");
    setBaseItem(null);
    setNarrowing({});
    adapter
      .getCatalogueWithItems(sourceScope)
      .then((view) => {
        setSourceItems(view?.items ?? []);
      })
      .finally(() => setLoadingItems(false));
  }, [adapter, sourceScope]);

  const selectSourceScope = (id: string) => {
    setSourceScope(id);
    setBaseItemId("");
    setBaseItem(null);
    setName("");
    setNarrowing({});
  };

  const selectBase = (id: string) => {
    setBaseItemId(id);
    if (!id) {
      setBaseItem(null);
      setName("");
      setNarrowing({});
      return;
    }
    setLoadingBase(true);
    // Load the full resolved item — the space this derivation may narrow.
    adapter
      .getResolvedCatalogueItem(id)
      .then((result) => {
        if (!result) return;
        setBaseItem(result.item);
        setName(result.item.name);
        setNarrowing({});
      })
      .finally(() => setLoadingBase(false));
  };

  const canCreate = baseItemId.length > 0 && !loadingBase && !!baseItem;

  const create = () => {
    if (!canCreate || !baseItem) return;
    onCreate(
      newItemDerivedFrom(baseItem, {
        id: `new-${currentTime().getTime()}`,
        name: name || baseItem.name,
        catalogueId: scope,
        narrowing,
      }),
    );
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3 items-start">
      <div className="space-y-6 lg:col-span-2">
        <p className="text-sm text-muted-foreground">
          Derive from an existing item&apos;s spec — narrow its options and
          quantities, or pin one down. Never adds to what it already offers.
        </p>

        <Card className="p-5 space-y-4">
          <Field label="Catalogue">
            {loadingCatalogues ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : (
              <Select value={sourceScope} onChange={selectSourceScope}>
                {catalogues.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Based on">
            {loadingItems ? (
              <div className="text-sm text-muted-foreground">
                Loading items…
              </div>
            ) : (
              <Select value={baseItemId} onChange={selectBase}>
                <option value="">Select an item…</option>
                {sourceItems.map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.name}{" "}
                    {it.productTypeId == null
                      ? "(No in-house route)"
                      : `(PT ${it.productTypeId})`}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {loadingBase && (
            <div className="text-sm text-muted-foreground">
              Loading item details…
            </div>
          )}
          {baseItem && (
            <Field label="Name">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={baseItem.name}
                className="max-w-sm"
              />
            </Field>
          )}
        </Card>

        {baseItem && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Narrow {baseItem.name}</div>
            <NarrowingEditor
              parentFields={baseItem.fields}
              parentQuantityBreaks={baseItem.quantityBreaks}
              narrowing={narrowing}
              onChange={setNarrowing}
            />
          </div>
        )}
      </div>

      <aside className="lg:sticky lg:top-6">
        <Card className="p-5 space-y-4">
          <div className="text-sm text-muted-foreground">
            Added to the catalogue's working copy — save the catalogue to
            publish.
          </div>
          <div className="space-y-2">
            <Button
              size="lg"
              className="w-full"
              disabled={!canCreate}
              onClick={create}
            >
              {submitLabel}
            </Button>
            <Button variant="ghost" className="w-full" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </Card>
      </aside>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="text-sm text-muted-foreground">{label}</div>
      {children}
    </div>
  );
}

function Select({
  value,
  onChange,
  children,
}: {
  value: string | number;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="max-w-sm w-full px-3 py-2 rounded-md border border-border bg-card text-sm"
    >
      {children}
    </select>
  );
}
